/**
 * Peta route aplikasi.
 *
 * SEMUA halaman di-lazy-load, jadi bundel awal hanya shell + Layout.
 *
 * STRUKTUR URL (mobile-first, mengikuti mockup):
 *   /                      Beranda (dibuka lewat splash di root)
 *   /kamus                 Daftar kata + pencarian + filter
 *   /entri/:id             Detail satu kata
 *   /pelajar               Daftar bahasa untuk dipelajari
 *   /pelajar/:slug         Detail bahasa + menu topik
 *   /pelajar/:slug/kuis    Kuis pilihan ganda
 *   /pelajar/:slug/:topic  Daftar kata satu topik
 *   /kontributor           Wizard kontribusi (4 langkah)
 *   /kontributor/berhasil  Layar sukses setelah kirim
 *   /profil                Profil saya
 *   /favorit               Daftar favorit
 *   /tentang               Tentang aplikasi
 *   /masuk /daftar         Auth
 *   /selamat-datang        Onboarding (layar penuh, tanpa chrome)
 *
 * REDIRECT LAMA: route versi desktop (v1) dipetakan ke route baru supaya
 * tautan yang sudah dibagikan dan bookmark tidak mati.
 */

import { Suspense, lazy, useSyncExternalStore } from 'react'
import type { ReactElement } from 'react'
import { Navigate, Route, Routes, useParams } from 'react-router-dom'
import Layout from './components/Layout'
import { session } from './lib/session'
import type { SessionUser } from './lib/session'

const Kamus = lazy(() => import('./pages/Kamus'))
const EntryDetail = lazy(() => import('./pages/EntryDetail'))
const Belajar = lazy(() => import('./pages/Belajar'))
const BelajarDetail = lazy(() => import('./pages/BelajarDetail'))
const BelajarTopik = lazy(() => import('./pages/BelajarTopik'))
const Kuis = lazy(() => import('./pages/Kuis'))
const Kontributor = lazy(() => import('./pages/Kontributor'))
const KontributorBerhasil = lazy(() => import('./pages/KontributorBerhasil'))
const Profil = lazy(() => import('./pages/Profil'))
const Favorit = lazy(() => import('./pages/Favorit'))
const About = lazy(() => import('./pages/About'))

// Panel admin. Shell-nya sendiri (AdminShell) yang mengoordinasi sidebar,
// sakelar mode, dan guard peran - halaman-halamannya tidak tahu apa-apa soal
// navigasi, jadi bisa diuji satu per satu.
const AdminShell = lazy(() => import('./admin/AdminShell'))
const AdminOverviewPage = lazy(() => import('./admin/AdminOverviewPage'))
const AdminEntriesPage = lazy(() => import('./admin/AdminEntriesPage'))
const AdminLanguagesPage = lazy(() => import('./admin/AdminLanguagesPage'))
const AdminModerationPage = lazy(() => import('./admin/AdminModerationPage'))
const AdminUsersPage = lazy(() => import('./admin/AdminUsersPage'))
const AdminAuditPage = lazy(() => import('./admin/AdminAuditPage'))
const Login = lazy(() => import('./pages/Login'))
const Register = lazy(() => import('./pages/Register'))
const NotFound = lazy(() => import('./pages/NotFound'))
const SelamatDatang = lazy(() => import('./pages/SelamatDatang'))
const Root = lazy(() => import('./pages/Root'))

/** Fallback Suspense. */
function Loading() {
  return <div className="p-8 text-center opacity-60">Memuat...</div>
}

/**
 * Baca sesi dari store modul `lib/session.ts` dan re-render saat berubah.
 *
 * `useSyncExternalStore` dipakai supaya snapshot-nya konsisten dengan
 * StrictMode (tidak ada tearing seperti `useState` + subscribe manual), dan
 * server snapshot-nya `null` supaya render pertama tidak hydration-mismatch.
 */
/**
 * Status sesi: apakah user-nya siapa DAN apakah server sudah dijawab.
 *
 * Tanpa bagian kedua ini ada race yang nyata: setelah refresh di /favorit,
 * `getUser()` masih null karena `/auth/me` belum selesai, guard langsung
 * mengarahkan ke /masuk - padahal cookie-nya sah. Mobile paling sering
 * reload halaman, jadi ini bukan kasus tepi.
 */
function useSession(): { user: SessionUser | null; checked: boolean } {
  // `getSnapshot` DIPERLUKAN stabil: kalau ia mengembalikan objek baru tiap
  // panggilan, React menganggap snapshot selalu berubah dan render berjalan
  // tanpa henti. Karena itu dua nilai diambil terpisah lewat selector yang
  // masing-masing mengembalikan primitif.
  const user = useSyncExternalStore(
    (onChange) => session.subscribe(onChange),
    () => session.getUser(),
    () => null,
  )
  const checked = useSyncExternalStore(
    (onChange) => session.subscribe(onChange),
    () => session.isChecked(),
    () => false,
  )
  return { user, checked }
}

/**
 * Guard route yang butuh sesi. `children` SELALU elemen yang sudah di-lazy-load
 * (bukan komponen inline), supaya guard tidak menambah chunk baru.
 *
 * `next` disimpan di query string supaya setelah login user kembali ke halaman
 * yang tadi dicoba, bukan landing page.
 */
function Protected({ children }: { children: ReactElement }): ReactElement {
  const { user, checked } = useSession()

  // Belum tahu jawabannya: tampilkan memuat, jangan putuskan apa pun.
  if (!checked) return <Loading />

  if (!user) {
    const path =
      typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''
    // `replace` supaya tombol "back" tidak terjebak di halaman yang butuh login.
    return <Navigate to={`/masuk?next=${encodeURIComponent(path)}`} replace />
  }
  return children
}

/**
 * Redirect dari route lama yang punya parameter. `<Navigate to="/x/:slug">` tidak
 * bisa menyalin parameter, jadi pemetaannya dilakukan di sini lewat `useParams`.
 */
function LegacyRedirect({
  build,
  fallback = '/',
}: {
  build: (params: Record<string, string | undefined>) => string
  fallback?: string
}) {
  const params = useParams()
  return <Navigate to={build(params) || fallback} replace />
}

export default function App() {
  return (
    <>
      {/* Layar penuh tanpa chrome (splash + onboarding) hidup di router sendiri:
          `Layout` sengaja TIDAK membungkus `<Routes>` di sini, karena kalau
          begitu tidak ada cara menampilkan sebagian route tanpa tab bar. */}
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/selamat-datang" element={<SelamatDatang />} />

          {/*
            Panel admin SENGAJA di luar wildcard Layout.

            Layout itu shell web publik: sidebar pengguna, tab bar bawah,
            footer. Panel admin punya chrome-nya sendiri (AdminShell), dan
            tidak boleh mewarisi sidebar situs - admin yang sedang moderasi
            entri tidak butuh tautan "Home" dan "Kontributor" di samping
            panel kerjanya.

            Guard peran tetap di dalam AdminShell (dan di server); di sini
            cukup supaya panel tidak mewarisi chrome situs publik.
          */}
          {/*
            Route lama `/moderasi`. Moderasi kini bagian dari panel admin, dan
            halaman lamanya dihapus: dua tempat untuk satu tugas membuat
            moderator menyetujui di satu layar lalu mencari jejaknya di layar
            lain. Redirect ini ada di router ATAS, bukan di dalam wildcard
            Layout, supaya shell situs publik tidak sempat ter-render dulu.
          */}
          <Route path="/moderasi" element={<Navigate to="/admin/moderasi" replace />} />

          <Route path="/admin" element={<AdminShell />}>
            <Route index element={<AdminOverviewPage />} />
            <Route path="entri" element={<AdminEntriesPage />} />
            <Route path="bahasa" element={<AdminLanguagesPage />} />
            <Route path="moderasi" element={<AdminModerationPage />} />
            <Route path="pengguna" element={<AdminUsersPage />} />
            <Route path="audit" element={<AdminAuditPage />} />
          </Route>
          <Route
            path="*"
            element={
              <Layout>
                <Suspense fallback={<Loading />}>
                  <AppRoutes />
                </Suspense>
              </Layout>
            }
          />
        </Routes>
      </Suspense>
    </>
  )
}

/**
 * Route di dalam Layout. Dipisah sebagai komponen sendiri supaya `Layout`
 * hanya dirender satu kali di outlet `*`, bukan di dua tempat.
 */
function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Root />} />
      <Route path="/kamus" element={<Kamus />} />
      <Route path="/entri/:id" element={<EntryDetail />} />

      <Route path="/pelajar" element={<Belajar />} />
      <Route path="/pelajar/:slug" element={<BelajarDetail />} />
      {/* /kuis didahulukan: tanpa ini React Router akan mencocokkan "kuis"
          sebagai nilai param :topic. */}
      <Route path="/pelajar/:slug/kuis" element={<Kuis />} />
      <Route path="/pelajar/:slug/:topic" element={<BelajarTopik />} />

      <Route path="/kontributor" element={<Kontributor />} />
      <Route
        path="/kontributor/berhasil"
        element={
          <Protected>
            <KontributorBerhasil />
          </Protected>
        }
      />

      <Route
        path="/profil"
        element={
          <Protected>
            <Profil />
          </Protected>
        }
      />
      <Route
        path="/favorit"
        element={
          <Protected>
            <Favorit />
          </Protected>
        }
      />

      {/*
        Halaman moderator. Tidak dibungkus <Protected adminOnly>:-route publik
        lebih sederhana dan halaman ini sudah menolak non-admin sendiri dengan
        pesan yang jelas. Penjaga yang sesungguhnya ada di API (`requireAdmin`),
        jadi menambahkan gerbang di sini tidak menambah keamanan, hanya
        menambah tempat untuk salah.
      */}


      <Route path="/tentang" element={<About />} />
      <Route path="/masuk" element={<Login />} />
      <Route path="/daftar" element={<Register />} />

      {/* ---- Redirect route versi lama (v1) ----
          `replace` supaya tidak menumpuk di riwayat navigasi. */}
      <Route
        path="/bahasa/:slug"
        element={<LegacyRedirect build={(p) => `/pelajar/${encodeURIComponent(p.slug ?? '')}`} />}
      />
      <Route
        path="/daerah/:slug"
        element={
          <LegacyRedirect
            build={(p) => `/kamus?region=${encodeURIComponent(p.slug ?? '')}`}
          />
        }
      />
      <Route path="/tambah" element={<Navigate to="/kontributor" replace />} />
      <Route path="/saya" element={<Navigate to="/profil" replace />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}